import React, { useContext, useEffect, useState, useRef } from "react";
import { ChevronDown, Star } from "lucide-react";
import axios from "axios";
import GreenStar from "../../../images/Builder/green_star.svg";
import GrayStar from "../../../images/Builder/gray_star.svg";
import { useBuilderContext } from "../context/BuilderContext";
import { useApplyPreBuiltStrategy } from "../helpers/ApplyPrebuilt";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import MetricsComputations from "../../../components/MetricsComputations";
import { getCookie } from "../../../utils/cookieUtils";

const StrategyCard = ({ strategy, onTap, symbolData, hasInitialPrices, initialPrices, initialPricesSymbol, currentSymbol }) => {
  // Add safety check
  if (!strategy || !strategy.instruments) {
    return (
      <div className="bg-p400 rounded-lg py-3 w-[360px] flex-shrink-0 h-[165px] flex items-center justify-center">
        <span className="text-n500">Loading strategy...</span>
      </div>
    );
  }

  // Helper to get LTP - use initial prices to avoid continuous updates
  const getLTP = (code) => {
    if (!code) return "−";

    // Only use initial prices if they belong to current symbol
    const useInitialPrices = hasInitialPrices && initialPricesSymbol === currentSymbol;

    // Use initial prices if available and valid, otherwise fall back to symbolData
    if (useInitialPrices && initialPrices && initialPrices[code] !== undefined) {
      return initialPrices[code] > 0 ? initialPrices[code] : "−";
    }

    // Fallback to symbolData for initial loading
    if (!symbolData) return "−";
    const price = symbolData[code]?.ltp || symbolData[code]?.prev_close || 0;
    return price > 0 ? price : "−";
  };

  // Format profit/loss values - simplified to match Flutter logic
  const formatValue = (value) => {
    if (
      !value ||
      value === "Calculating..." ||
      value === "N/A" ||
      value === "Error"
    ) {
      return value;
    }

    if (value === "Unlimited") {
      return "Unlimited";
    }

    if (typeof value === "number") {
      if (value === Infinity || value === -Infinity) {
        return "Unlimited";
      }
      return `₹${Math.abs(value).toLocaleString()}`;
    }

    // Handle string values that might contain currency symbols
    if (typeof value === "string" && value.includes("₹")) {
      return value;
    }

    return value;
  };

  return (
    <>
      <div
        className="bg-p400 rounded-lg py-3 cursor-pointer hover:shadow-md transition-shadow duration-200 w-[360px] flex-shrink-0 h-[165px]"
        onClick={() => onTap(strategy)}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5 px-3">
            {strategy.tag && (
              <div className="bg-p400 rounded py-0.5 flex items-center">
                <div>
                  {strategy.tag === "Recommended" ? (
                    <img src={GreenStar} alt="Recommended" />
                  ) : (
                    <img src={GrayStar} alt="low margin/hedge" />
                  )}
                </div>

                <div
                  className={`ms-1 text-sm font-medium ${strategy.tag === "Recommended" ? "text-p150" : "text-n500"}`}>
                  {strategy.tag}
                </div>
              </div>
            )}
            <span className="text-xs font-medium text-n500 ml-auto">
              Tap to select
            </span>
          </div>

          {/* Instruments */}
          <div className="flex-1">
            {strategy.instruments && strategy.instruments.length > 0 ? (
              strategy.instruments.map((instrument, index) => (
                <div key={index} className="flex items-center mb-2 px-3">
                  <div
                    className={`px-2 py-1 rounded text-[10px] font-medium mr-1 ${instrument.isBuy
                      ? "bg-[#12B16E26] text-p150"
                      : "bg-[#E94C6929] text-red"
                      }`}>
                    {instrument.isBuy ? "Buy" : "Sell"}
                  </div>
                  <span className="flex-1 text-sm font-medium text-n900 mx-1">
                    {instrument.quantity} X {instrument.instrumentName}
                  </span>
                  <span className="text-sm font-medium text-n900">
                    {getLTP(instrument.id)}
                  </span>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-full text-n500">
                Brining the best strategies for you...
              </div>
            )}
          </div>

          <div className="h-px bg-n200 mb-2"></div>

          <div className="flex items-center justify-between px-3">
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-n400 mb-1">
                Margin required
              </span>
              <span className="text-xs font-medium text-p300">
                {strategy.capital === "No instruments" || strategy.capital === "Error"
                  ? "−"
                  : strategy.capital === "Waiting for prices..." || strategy.capital === "Loading futures data..."
                    ? "Loading..."
                    : strategy.isCalculatingMargin
                      ? "Calculating..."
                      : `₹ ${strategy.capital}`}
              </span>
            </div>

            <div className="w-px h-7 bg-n200"></div>

            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-n400 mb-1">
                Max Profit
              </span>
              <span className="text-xs font-medium text-p150">
                {strategy.maxProfit === "No instruments" || strategy.maxProfit === "Error"
                  ? "−"
                  : strategy.maxProfit === "Waiting for prices..." || strategy.maxProfit === "Loading futures data..."
                    ? "Loading..."
                    : strategy.isCalculatingMetrics
                      ? "Calculating..."
                      : formatValue(strategy.maxProfit)}
              </span>
            </div>

            <div className="w-px h-7 bg-n200"></div>

            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-n400 mb-1">
                Max Loss
              </span>
              <span className="text-xs font-medium text-red">
                {strategy.maxLoss === "No instruments" || strategy.maxLoss === "Error"
                  ? "−"
                  : strategy.maxLoss === "Waiting for prices..." || strategy.maxLoss === "Loading futures data..."
                    ? "Loading..."
                    : strategy.isCalculatingMetrics
                      ? "Calculating..."
                      : formatValue(strategy.maxLoss)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const HedgeStrategiesContainer = ({
  activeCategory = "bullish",
  onStrategySelect,
  scrollToTop,
}) => {
  const {
    selectedSymbol,
    currentOptionsData,
    currentFuturesData,
    selectedExpiry,
    marketType = "MIS",
  } = useBuilderContext();
  const broker = localStorage.getItem("broker");
  const access_token = getCookie("access_token");
  const client_id = getCookie("client_id");
  const { symbolData, subscribeToSymbols } = useContext(
    WebsocketWrapperContext
  );
  const { applyHedgeStrategy } = useApplyPreBuiltStrategy();

  const [strategies, setStrategies] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [subscribedCodes, setSubscribedCodes] = useState(new Set());
  const [initialPrices, setInitialPrices] = useState({});
  const [hasInitialPrices, setHasInitialPrices] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const calculatedStrategiesRef = useRef(new Set());
  const isCalculatingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const [hasUnderlyingPrice, setHasUnderlyingPrice] = useState(false);
  const [isRouteNavigating, setIsRouteNavigating] = useState(false);
  const [persistedStrategies, setPersistedStrategies] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Add ref to track current symbol to prevent race conditions
  const currentSymbolRef = useRef(null);
  const currentCategoryRef = useRef(null);
  // Track which symbol the initialPrices belong to
  const initialPricesSymbolRef = useRef(null);

  // Initialize metrics computations
  const metricsComputations = MetricsComputations();

  // Initialize component with fallback strategies
  useEffect(() => {
    if (!hasInitialized && selectedSymbol?.scrip) {
      setHasInitialized(true);

      // If no strategies are loaded, create initial fallback strategies
      if (strategies.length === 0) {
        const initialStrategies = [
          { id: `${activeCategory}_recommended`, tag: "Recommended", instruments: [], rawInstruments: [], capital: "Initializing...", maxProfit: "Initializing...", maxLoss: "Initializing...", isCalculatingMargin: false, isCalculatingMetrics: false },
          { id: `${activeCategory}_low_margin`, tag: "Low margin", instruments: [], rawInstruments: [], capital: "Initializing...", maxProfit: "Initializing...", maxLoss: "Initializing...", isCalculatingMargin: false, isCalculatingMetrics: false },
          { id: `${activeCategory}_low_hedge_cost`, tag: "Low hedge cost", instruments: [], rawInstruments: [], capital: "Initializing...", maxProfit: "Initializing...", maxLoss: "Initializing...", isCalculatingMargin: false, isCalculatingMetrics: false },
        ];
        setStrategies(initialStrategies);
      }
    }
  }, [selectedSymbol?.scrip, hasInitialized, strategies.length, activeCategory]);

  // Detect route navigation by monitoring selectedSymbol changes
  useEffect(() => {
    if (!selectedSymbol) {
      setIsRouteNavigating(true);
    } else {
      // Add a small delay to ensure we're not in the middle of route navigation
      const timeoutId = setTimeout(() => {
        setIsRouteNavigating(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedSymbol]);

  // Persist strategies to localStorage when they change
  useEffect(() => {
    if (strategies.length > 0 && selectedSymbol?.scrip) {
      const key = `hedge_strategies_${selectedSymbol.scrip}_${activeCategory}`;
      localStorage.setItem(key, JSON.stringify(strategies));
      setPersistedStrategies(strategies);
    }
  }, [strategies, selectedSymbol?.scrip, activeCategory]);

  // Restore strategies from localStorage on mount or when symbol changes
  useEffect(() => {
    if (selectedSymbol?.scrip && !isRouteNavigating) {
      const key = `hedge_strategies_${selectedSymbol.scrip}_${activeCategory}`;
      const stored = localStorage.getItem(key);
      if (stored && strategies.length === 0) {
        try {
          const parsedStrategies = JSON.parse(stored);
          if (Array.isArray(parsedStrategies) && parsedStrategies.length > 0) {
            setStrategies(parsedStrategies);
            setPersistedStrategies(parsedStrategies);
          }
        } catch (error) {
        }
      }
    }
  }, [selectedSymbol?.scrip, activeCategory, isRouteNavigating]);

  // Fallback: Ensure we always have some strategies to show
  useEffect(() => {
    if (selectedSymbol?.scrip && strategies.length === 0 && !isLoading && !isRouteNavigating) {
      const fallbackStrategies = [
        { id: `${activeCategory}_recommended`, tag: "Recommended", instruments: [], rawInstruments: [], capital: "Loading...", maxProfit: "Loading...", maxLoss: "Loading...", isCalculatingMargin: false, isCalculatingMetrics: false },
        { id: `${activeCategory}_low_margin`, tag: "Low margin", instruments: [], rawInstruments: [], capital: "Loading...", maxProfit: "Loading...", maxLoss: "Loading...", isCalculatingMargin: false, isCalculatingMetrics: false },
        { id: `${activeCategory}_low_hedge_cost`, tag: "Low hedge cost", instruments: [], rawInstruments: [], capital: "Loading...", maxProfit: "Loading...", maxLoss: "Loading...", isCalculatingMargin: false, isCalculatingMetrics: false },
      ];
      setStrategies(fallbackStrategies);
    }
  }, [selectedSymbol?.scrip, strategies.length, isLoading, isRouteNavigating, activeCategory]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 360 + 12; // card width + gap
      const newPosition = Math.max(0, scrollPosition - cardWidth);
      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 360 + 12; // card width + gap
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newPosition = Math.min(maxScroll, scrollPosition + cardWidth);
      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const handleScroll = () => {
        setScrollPosition(container.scrollLeft);
      };
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);



  // Helper to format instrument name
  const formatInstrumentName = (instr) => {
    if (!instr) return "Instrument not found";

    if (instr.isFuture) {
      return instr.tradingSymbol || `${selectedSymbol?.symbol || "?"} FUT`;
    }

    const strike = instr.strike || "?";
    const type = instr.side === "Call" ? "CE" : "PE";
    const symbol = selectedSymbol?.symbol || selectedSymbol?.scrip || "?";
    return `${symbol} ${strike} ${type}`;
  };

  // Helper to format expiry date 
  const formatExpiryDate = (expiry) => {
    if (!expiry) return "";
    return expiry.replaceAll("%20", " ");
  };

  // Convert raw instruments to selectedData format for metrics computation
  const convertToSelectedDataFormat = React.useCallback(
    (rawInstruments) => {
      if (!rawInstruments || !selectedSymbol) return [];

      // Validate initialPrices belong to current symbol
      const useInitialPrices = hasInitialPrices && initialPricesSymbolRef.current === selectedSymbol?.scrip;

      const transformedRows = [];

      for (const instrument of rawInstruments) {
        if (instrument.isFuture) {
          // Handling futures - represent as a single future instrument
          const futurePrice = useInitialPrices
            ? (initialPrices[instrument.code] || 0)
            : (symbolData[instrument.code]?.ltp || 0);
          const expiry =
            selectedSymbol.futExpiries?.length > 0
              ? selectedSymbol.futExpiries[0]
              : selectedSymbol.fut_expiries?.length > 0
                ? selectedSymbol.fut_expiries[0]
                : selectedExpiry;

          if (!expiry) continue;

          // Get lot_size from first available options expiry (lot sizes are same across expiries)
          const firstOptionExpiry = Object.keys(currentOptionsData)[0];
          const lotMultiplier =
            currentOptionsData[firstOptionExpiry]?.options?.[0]?.call?.lot_size ||
            50;

          // Create a single future entry instead of call/put combination
          transformedRows.push({
            expiry: formatExpiryDate(expiry),
            scrip: selectedSymbol.scrip,
            lotSize: instrument.quantity || 1, // Use quantity (number of lots) from instrument
            code: instrument.code,
            tradingSymbol: instrument.tradingSymbol || "",
            freeze_qty: 0,
            exchange: instrument.exchange || "NFO",
            lot_multiplier: lotMultiplier,
            isCALL: true, // Use true for future, will be handled specially in metrics
            isBuy: instrument.buy_sell === "Buy",
            strike: 0, // For futures, strike is not used in calculations
            price: futurePrice.toString(), // Entry price for futures
            targetPrice: "NaN",
            isFuture: true, // Add flag to identify futures
          });
        } else {
          // Handle options
          const optionPrice = useInitialPrices
            ? (initialPrices[instrument.code] || 0)
            : (symbolData[instrument.code]?.ltp || 0);
          const strikeValue = parseInt(instrument.strike) || 0;
          const isCall = instrument.side === "Call";
          const optionExpiry =
            selectedSymbol.optionExpiries?.length > 0
              ? selectedSymbol.optionExpiries[0]
              : selectedSymbol.option_expiries?.length > 0
                ? selectedSymbol.option_expiries[0]
                : selectedExpiry;

          if (!optionExpiry) continue;

          // Get lot_size from first available options expiry (lot sizes are same across expiries)
          const firstOptionExpiry = Object.keys(currentOptionsData)[0];
          const lotMultiplier =
            currentOptionsData[firstOptionExpiry]?.options?.[0]?.call?.lot_size ||
            50;

          transformedRows.push({
            isBuy: instrument.buy_sell === "Buy",
            isCALL: isCall,
            strike: strikeValue.toString(),
            expiry: formatExpiryDate(optionExpiry),
            scrip: selectedSymbol.scrip,
            lotSize: instrument.quantity || 1, // Use quantity (number of lots) from instrument
            code: instrument.code,
            tradingSymbol: instrument.tradingSymbol || "",
            freeze_qty: 0,
            exchange: instrument.exchange || "NFO",
            lot_multiplier: lotMultiplier,
            targetPrice: "NaN",
            price: optionPrice.toString(),
            isFuture: false, // Add flag to identify options
          });
        }
      }

      return transformedRows;
    },
    [selectedSymbol, symbolData, currentOptionsData, selectedExpiry, hasInitialPrices, initialPrices]
  );

  // metrics calculation - only max profit and max loss
  const calculateMetricsForStrategy = React.useCallback(
    async (strategy, rawInstruments, capturedSymbol, capturedCategory) => {
      // Validate that we're still on the same symbol and category
      if (
        !rawInstruments ||
        rawInstruments.length === 0 ||
        !selectedSymbol ||
        !symbolData[selectedSymbol?.code] ||
        capturedSymbol !== currentSymbolRef.current ||
        capturedCategory !== currentCategoryRef.current
      ) {
        return {
          maxProfit: "0",
          maxLoss: "0",
          aborted: true,
        };
      }

      // Validate initialPrices belong to current symbol
      const useInitialPrices = hasInitialPrices && initialPricesSymbolRef.current === capturedSymbol;

      try {
        // Convert raw instruments to selectedData format using Flutter logic
        const transformedRows = convertToSelectedDataFormat(rawInstruments);

        if (transformedRows.length === 0) {
          return {
            maxProfit: "0",
            maxLoss: "0",
          };
        }

        // Get current LTP - use initial price if available and valid for current symbol
        const currentLtp = useInitialPrices
          ? (initialPrices[selectedSymbol?.code] || symbolData[selectedSymbol?.code]?.ltp)
          : symbolData[selectedSymbol?.code]?.ltp;

        if (!currentLtp || currentLtp <= 0) {
          return {
            maxProfit: "Calculating...",
            maxLoss: "Calculating...",
          };
        }

        // lot size - use futures lot size if available, otherwise fall back to options lot size
        const lotsVal =
          currentOptionsData[selectedExpiry]?.futures?.[0]?.lot_size ||
          currentOptionsData[selectedExpiry]?.options?.[0]?.call?.lot_size ||
          50;

        // Compute metrics - get first available expiry for option strikes data
        const firstOptionExpiry = Object.keys(currentOptionsData)[0];
        const metrics = await metricsComputations.computeXandYAxis({
          transformedSelectedData: transformedRows,
          ltp: currentLtp,
          step: selectedSymbol.step,
          lotsVal: lotsVal,
          currentOptionsData: currentOptionsData,
          selectedExpiry: firstOptionExpiry, // Use first expiry for strikes (all expiries have same strikes)
        });

        // Validate symbol didn't change during async computation
        if (capturedSymbol !== currentSymbolRef.current || capturedCategory !== currentCategoryRef.current) {
          return {
            maxProfit: "0",
            maxLoss: "0",
            aborted: true,
          };
        }


        let maxProfit = "0";
        let maxLoss = "0";

        // Handle maxProfit
        if (metrics.maxProfit !== null && metrics.maxProfit !== undefined) {
          const maxProfitValue = metrics.maxProfit;

          // "Unlimited" check, giving NaN otherwise
          if (
            typeof maxProfitValue === "string" &&
            maxProfitValue === "Unlimited"
          ) {
            maxProfit = "Unlimited";
          } else if (typeof maxProfitValue === "number") {
            if (maxProfitValue === Infinity || maxProfitValue === -Infinity) {
              maxProfit = "Unlimited";
            } else {
              // maxProfit is already absolute from MetricsComputations
              maxProfit = `₹${maxProfitValue.toFixed(2)}`;
            }
          }
        }

        // Handle maxLoss
        if (metrics.maxLoss !== null && metrics.maxLoss !== undefined) {
          const maxLossValue = metrics.maxLoss;

          // Check if it's already a string "Unlimited"
          if (
            typeof maxLossValue === "string" &&
            maxLossValue === "Unlimited"
          ) {
            maxLoss = "Unlimited";
          } else if (typeof maxLossValue === "number") {
            if (maxLossValue === Infinity || maxLossValue === -Infinity) {
              maxLoss = "Unlimited";
            } else {
              maxLoss = `₹${Math.abs(maxLossValue).toFixed(2)}`;
            }
          }
        }

        return {
          maxProfit,
          maxLoss,
        };
      } catch (error) {
        return {
          maxProfit: "0",
          maxLoss: "0",
        };
      }
    },
    [
      symbolData,
      selectedSymbol,
      currentOptionsData,
      selectedExpiry,
      metricsComputations,
      convertToSelectedDataFormat,
      hasInitialPrices,
      initialPrices,
    ]
  );

  //margin calculation
  const calculateMarginForStrategy = React.useCallback(
    async (strategy, rawInstruments, capturedSymbol, capturedCategory) => {
      // Validate that we're still on the same symbol and category
      if (
        !rawInstruments ||
        rawInstruments.length === 0 ||
        capturedSymbol !== currentSymbolRef.current ||
        capturedCategory !== currentCategoryRef.current
      ) {
        return { capital: "0", aborted: true };
      }

      // Validate initialPrices belong to current symbol
      const useInitialPrices = hasInitialPrices && initialPricesSymbolRef.current === capturedSymbol;

      try {
        // Generate order list similar to working component
        const orderList = rawInstruments.map((instrument) => {
          const price = useInitialPrices
            ? (initialPrices[instrument.code] || 0)
            : (symbolData[instrument.code]?.ltp ||
              symbolData[instrument.code]?.prev_close ||
              0);

          if (instrument.isFuture) {
            return {
              isBuy: instrument.buy_sell === "Buy",
              isCall: false, // neither call nor put
              isFut: true,
              code: instrument.code,
              exchange: instrument.exchange || "NFO",
              productType: marketType === "NRML" ? "NRML" : "MIS",
              orderType: "MARKET",
              quantity:
                1 *
                (currentOptionsData[selectedExpiry]?.options?.[0]?.call
                  ?.lot_size || 50),
              price: price,
              strike: 0,
              expiry:
                selectedSymbol?.futExpiries?.[0] ||
                selectedSymbol?.fut_expiries?.[0] ||
                "",
              tradingSymbol: instrument.tradingSymbol || "",
              // Get lot_size from first available options expiry (lot sizes are same across expiries)
              lot_multiplier: (() => {
                const firstOptionExpiry = Object.keys(currentOptionsData)[0];
                return currentOptionsData[firstOptionExpiry]?.options?.[0]?.call
                  ?.lot_size || 50;
              })(),
              scrip: selectedSymbol?.scrip,
            };
          } else {
            // For options
            const strikeValue = parseInt(instrument.strike) || 0;

            return {
              isBuy: instrument.buy_sell === "Buy",
              isCall: instrument.side === "Call",
              isFut: false,
              code: instrument.code,
              exchange: instrument.exchange || "NFO",
              productType: marketType === "NRML" ? "NRML" : "MIS",
              orderType: "MARKET",
              quantity:
                1 *
                (currentOptionsData[selectedExpiry]?.options?.[0]?.call
                  ?.lot_size || 50),
              price: price,
              strike: strikeValue,
              expiry:
                selectedSymbol?.optionExpiries?.[0] ||
                selectedSymbol?.option_expiries?.[0] ||
                selectedExpiry,
              tradingSymbol: instrument.tradingSymbol || "",
              // Get lot_size from first available options expiry (lot sizes are same across expiries)
              lot_multiplier: (() => {
                const firstOptionExpiry = Object.keys(currentOptionsData)[0];
                return currentOptionsData[firstOptionExpiry]?.options?.[0]?.call
                  ?.lot_size || 50;
              })(),
              scrip: selectedSymbol?.scrip,
            };
          }
        });

        // Check if all prices are available 
        const hasUndefinedPrice = orderList.some(
          (order) => order.price === undefined || order.price === 0
        );

        if (hasUndefinedPrice) {
          return { capital: "Calculating..." };
        }

        // console.log("Order list for margin calculation:", orderList); 

        // Call margin API (SC Compnent)
        const payload = {
          order_list: orderList,
          broker: broker,
          access_token: access_token,
          client_id: client_id,
        };

        // console.log("Margin API payload:", payload); 

        const marginResponse = await axios.post(
          "https://7mwh6csmrh.execute-api.ap-south-1.amazonaws.com/default/og_allbrokers_margin",
          payload
        );

        // Validate symbol didn't change during API call
        if (capturedSymbol !== currentSymbolRef.current || capturedCategory !== currentCategoryRef.current) {
          return { capital: "0", aborted: true };
        }

        const marginRequired = marginResponse.data.marginRequired || "0";


        if (marginResponse.data.marginRequired == null) {
          return { capital: "Calculating..." };
        }


        if (marginRequired === "--" || marginRequired === "") {
          return { capital: "0" };
        }

        const marginValue =
          parseFloat(marginRequired.toString().replace(/,/g, "")) || 0;

        if (marginValue <= 0) {
          return { capital: "0" };
        }


        const formattedValue = marginValue.toLocaleString("en-IN");

        return { capital: formattedValue };
      } catch (error) {
        return { capital: "0" };
      }
    },
    [
      symbolData,
      selectedSymbol,
      currentOptionsData,
      selectedExpiry,
      marketType,
      broker,
      access_token,
      client_id,
      hasInitialPrices,
      initialPrices,
    ]
  );

  // Track when underlying price becomes available
  useEffect(() => {
    if (!selectedSymbol?.code || !symbolData) {
      setHasUnderlyingPrice(false);
      return;
    }

    const underlyingPrice = symbolData[selectedSymbol.code]?.ltp || symbolData[selectedSymbol.code]?.prev_close;
    const priceAvailable = underlyingPrice && underlyingPrice > 0;

    if (priceAvailable && !hasUnderlyingPrice) {
      setHasUnderlyingPrice(true);
    } else if (!priceAvailable && hasUnderlyingPrice) {
      setHasUnderlyingPrice(false);
    }
  }, [selectedSymbol?.code, symbolData]); // Removed hasUnderlyingPrice from dependencies to avoid infinite loop

  // Generate strategies and subscribe to symbols
  useEffect(() => {
    const generateStrategies = async () => {
      // Update current category ref to track race conditions
      currentCategoryRef.current = activeCategory;
      currentSymbolRef.current = selectedSymbol?.scrip;

      // Always ensure we have some strategies to show, even if data is missing
      if (!selectedSymbol || !currentOptionsData || !selectedExpiry) {

        // If we don't have strategies and we're not navigating, create fallback strategies
        if (strategies.length === 0 && !isRouteNavigating) {
          const fallbackStrategies = [
            { id: `${activeCategory}_recommended`, tag: "Recommended", instruments: [], rawInstruments: [], capital: "Loading data...", maxProfit: "Loading data...", maxLoss: "Loading data...", isCalculatingMargin: false, isCalculatingMetrics: false },
            { id: `${activeCategory}_low_margin`, tag: "Low margin", instruments: [], rawInstruments: [], capital: "Loading data...", maxProfit: "Loading data...", maxLoss: "Loading data...", isCalculatingMargin: false, isCalculatingMetrics: false },
            { id: `${activeCategory}_low_hedge_cost`, tag: "Low hedge cost", instruments: [], rawInstruments: [], capital: "Loading data...", maxProfit: "Loading data...", maxLoss: "Loading data...", isCalculatingMargin: false, isCalculatingMetrics: false },
          ];
          setStrategies(fallbackStrategies);
        }
        return;
      }

      // Check if futures data is available for hedge strategies
      if (!currentFuturesData) {
        // Set strategies to show waiting state when futures data is not available
        const categoryStrategies = {
          bullish: [
            { id: "bullish_recommended", tag: "Recommended" },
            { id: "bullish_low_margin", tag: "Low margin" },
            { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
          ],
          bearish: [
            { id: "bearish_recommended", tag: "Recommended" },
            { id: "bearish_low_margin", tag: "Low margin" },
            { id: "bearish_low_hedge_cost", tag: "Low hedge cost" },
          ],
          neutral: [
            { id: "neutral_recommended", tag: "Recommended" },
            { id: "neutral_low_margin", tag: "Low margin" },
            { id: "neutral_low_hedge_cost", tag: "Low hedge cost" },
          ],
          saved: [
            { id: "bullish_recommended", tag: "Recommended" },
            { id: "bullish_low_margin", tag: "Low margin" },
            { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
          ]
        };

        const waitingStrategies = categoryStrategies[activeCategory]?.map(strategyDef => ({
          ...strategyDef,
          instruments: [],
          rawInstruments: [],
          capital: "Loading futures data...",
          maxProfit: "Loading futures data...",
          maxLoss: "Loading futures data...",
          isCalculatingMargin: false,
          isCalculatingMetrics: false,
        }));

        setStrategies(waitingStrategies);
        setSubscribedCodes(new Set());
        setIsLoading(false);
        return;
      }

      // Check if underlying price is available before proceeding
      const currentUnderlyingPrice = symbolData[selectedSymbol?.code]?.ltp || symbolData[selectedSymbol?.code]?.prev_close;
      const priceActuallyAvailable = currentUnderlyingPrice && currentUnderlyingPrice > 0;


      if (!hasUnderlyingPrice && !priceActuallyAvailable) {
        // Set strategies to show waiting state when underlying price is not available
        const categoryStrategies = {
          bullish: [
            { id: "bullish_recommended", tag: "Recommended" },
            { id: "bullish_low_margin", tag: "Low margin" },
            { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
          ],
          bearish: [
            { id: "bearish_recommended", tag: "Recommended" },
            { id: "bearish_low_margin", tag: "Low margin" },
            { id: "bearish_low_hedge_cost", tag: "Low hedge cost" },
          ],
          neutral: [
            { id: "neutral_recommended", tag: "Recommended" },
            { id: "neutral_low_margin", tag: "Low margin" },
            { id: "neutral_low_hedge_cost", tag: "Low hedge cost" },
          ],
          saved: [
            { id: "bullish_recommended", tag: "Recommended" },
            { id: "bullish_low_margin", tag: "Low margin" },
            { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
          ],
        };

        const waitingStrategies = categoryStrategies[activeCategory].map(strategyDef => ({
          ...strategyDef,
          instruments: [],
          rawInstruments: [],
          capital: "Waiting for price...",
          maxProfit: "Waiting for price...",
          maxLoss: "Waiting for price...",
          isCalculatingMargin: false,
          isCalculatingMetrics: false,
        }));

        setStrategies(waitingStrategies);
        setSubscribedCodes(new Set());
        setIsLoading(false);
        return;
      } else if (!hasUnderlyingPrice && priceActuallyAvailable) {
        setHasUnderlyingPrice(true);
        // Continue with strategy generation instead of returning
      }

      setIsLoading(true);

      // Define categoryStrategies outside try block so it's accessible in catch block
      const categoryStrategies = {
        bullish: [
          { id: "bullish_recommended", tag: "Recommended" },
          { id: "bullish_low_margin", tag: "Low margin" },
          { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
        ],
        bearish: [
          { id: "bearish_recommended", tag: "Recommended" },
          { id: "bearish_low_margin", tag: "Low margin" },
          { id: "bearish_low_hedge_cost", tag: "Low hedge cost" },
        ],
        neutral: [
          { id: "neutral_recommended", tag: "Recommended" },
          { id: "neutral_low_margin", tag: "Low margin" },
          { id: "neutral_low_hedge_cost", tag: "Low hedge cost" },
        ],
        saved: [
          { id: "bullish_recommended", tag: "Recommended" },
          { id: "bullish_low_margin", tag: "Low margin" },
          { id: "bullish_low_hedge_cost", tag: "Low hedge cost" },
        ],
      };

      try {

        const allCodesToSubscribe = new Set();
        const generated = await Promise.all(
          categoryStrategies[activeCategory].map(async (strategyDef) => {
            const retryWithDelay = async (attempts = 0) => {
              try {
                const instruments = applyHedgeStrategy(strategyDef.id) || [];

                // Filter out null instruments and check if we have valid instruments
                const validInstruments = instruments.filter(instr => instr && instr.code);

                const mappedInstruments = validInstruments.map((instr) => {
                  // codes for subscription
                  if (instr.code) {
                    allCodesToSubscribe.add(instr.code);
                  }

                  return {
                    id: instr.code,
                    instrumentName: formatInstrumentName(instr),
                    isBuy: instr.buy_sell === "Buy",
                    isFuture: instr.isFuture || false,
                    quantity: instr.quantity,
                    exchange: instr.exchange,
                  };
                });

                // If no valid instruments, return strategy with empty state
                if (validInstruments.length === 0) {
                  return {
                    ...strategyDef,
                    // Include symbol in strategy ID to prevent overwrites
                    id: `${strategyDef.id}_${selectedSymbol?.scrip}`,
                    originalId: strategyDef.id,
                    instruments: [],
                    rawInstruments: [],
                    capital: "No instruments",
                    maxProfit: "No instruments",
                    maxLoss: "No instruments",
                    isCalculatingMargin: false,
                    isCalculatingMetrics: false,
                  };
                }

                // Check if prices are available for this strategy before setting calculation flags
                const strategyPricesAvailable = validInstruments.length > 0 &&
                  symbolData[selectedSymbol?.code]?.ltp > 0 &&
                  validInstruments.every(instrument => {
                    if (!instrument.code) return false;
                    const price = symbolData[instrument.code]?.ltp || symbolData[instrument.code]?.prev_close;
                    return price !== undefined && price > 0;
                  });

                return {
                  ...strategyDef,
                  // Include symbol in strategy ID to prevent overwrites
                  id: `${strategyDef.id}_${selectedSymbol?.scrip}`,
                  originalId: strategyDef.id,
                  instruments: mappedInstruments,
                  rawInstruments: validInstruments,
                  capital: strategyPricesAvailable ? "Calculating..." : "Waiting for prices...",
                  maxProfit: strategyPricesAvailable ? "Calculating..." : "Waiting for prices...",
                  maxLoss: strategyPricesAvailable ? "Calculating..." : "Waiting for prices...",
                  isCalculatingMargin: strategyPricesAvailable,
                  isCalculatingMetrics: strategyPricesAvailable,
                };
              } catch (error) {
                // If retry also failed, return error state
                return {
                  ...strategyDef,
                  // Include symbol in strategy ID to prevent overwrites
                  id: `${strategyDef.id}_${selectedSymbol?.scrip}`,
                  originalId: strategyDef.id,
                  instruments: [],
                  rawInstruments: [],
                  capital: "Error",
                  maxProfit: "Error",
                  maxLoss: "Error",
                  isCalculatingMargin: false,
                  isCalculatingMetrics: false,
                  error: true,
                };
              }
            };

            return retryWithDelay();
          })
        );

        // Subscribing to all collected symbols
        if (allCodesToSubscribe.size > 0) {
          const codesToSubscribe = Array.from(allCodesToSubscribe);
          const newCodes = codesToSubscribe.filter(
            (code) => !subscribedCodes.has(code)
          );

          if (newCodes.length > 0) {
            // Group by exchange for subscription
            const exchangeGroups = {};
            generated.forEach((strategy) => {
              strategy.instruments.forEach((instr) => {
                if (newCodes.includes(instr.id) && instr.exchange) {
                  if (!exchangeGroups[instr.exchange]) {
                    exchangeGroups[instr.exchange] = [];
                  }
                  exchangeGroups[instr.exchange].push(instr.id);
                }
              });
            });

            // Subscribe to each exchange group
            Object.entries(exchangeGroups).forEach(([exchange, codes]) => {
              if (codes.length > 0) {
                subscribeToSymbols(codes, exchange);
              }
            });

            // Update subscribed codes
            setSubscribedCodes((prev) => new Set([...prev, ...newCodes]));
          }
        }

        // Ensure we always have some strategies to show, even if they're empty
        const finalStrategies = generated.length > 0 ? generated : categoryStrategies[activeCategory].map(strategyDef => ({
          ...strategyDef,
          instruments: [],
          rawInstruments: [],
          capital: "No data available",
          maxProfit: "No data available",
          maxLoss: "No data available",
          isCalculatingMargin: false,
          isCalculatingMetrics: false,
        }));

        // Reset initial prices BEFORE setting strategies to prevent race conditions
        setInitialPrices({});
        setHasInitialPrices(false);
        initialPricesSymbolRef.current = null;
        calculatedStrategiesRef.current.clear();
        isCalculatingRef.current = false;

        setStrategies(finalStrategies);
      } catch (error) {
        // Ensure we always have some strategies to show, even on error
        const fallbackStrategies = categoryStrategies[activeCategory].map(strategyDef => ({
          ...strategyDef,
          instruments: [],
          rawInstruments: [],
          capital: "Error loading",
          maxProfit: "Error loading",
          maxLoss: "Error loading",
          isCalculatingMargin: false,
          isCalculatingMetrics: false,
          error: true,
        }));
        setStrategies(fallbackStrategies);
      } finally {
        setIsLoading(false);
      }
    };

    generateStrategies();
  }, [activeCategory, selectedSymbol, currentOptionsData, currentFuturesData, hasUnderlyingPrice]); // Removed selectedExpiry to prevent regeneration when switching between options/futures view

  // Reset initial prices when selected symbol changes
  useEffect(() => {
    // Only clear strategies if we're actually changing to a different symbol
    // This prevents clearing strategies during route navigation when the same symbol is being re-initialized
    if (selectedSymbol && !isRouteNavigating) {
      // Update current symbol ref to track race conditions
      currentSymbolRef.current = selectedSymbol?.scrip;

      // Clear initial prices immediately to prevent race conditions
      setInitialPrices({});
      setHasInitialPrices(false);
      initialPricesSymbolRef.current = null;
      setHasUnderlyingPrice(false);
      calculatedStrategiesRef.current.clear();
      // Abort any in-progress calculations by resetting the flag
      isCalculatingRef.current = false;

      // Only clear strategies if we don't have any loaded yet and no persisted strategies
      // This prevents the "No hedge strategies available" message during route navigation
      if (strategies.length === 0 && persistedStrategies.length === 0) {
        setStrategies([]);
      }
    }
  }, [selectedSymbol, isRouteNavigating]);

  // Helper function to check if all required prices for a strategy are available
  const areStrategyPricesAvailable = React.useCallback((strategy) => {
    if (!strategy.rawInstruments || !symbolData || !selectedSymbol) return false;

    // First check if underlying price is available
    const underlyingPrice = symbolData[selectedSymbol.code]?.ltp || symbolData[selectedSymbol.code]?.prev_close;
    if (!underlyingPrice || underlyingPrice <= 0) return false;

    // Then check if all instrument prices are available
    return strategy.rawInstruments.every(instrument => {
      if (!instrument.code) return false;
      const price = symbolData[instrument.code]?.ltp || symbolData[instrument.code]?.prev_close;
      return price !== undefined && price > 0;
    });
  }, [symbolData, selectedSymbol]);

  // Capture initial prices when they're first available
  useEffect(() => {
    if (!strategies.length || !symbolData || hasInitialPrices) return;

    // Validate we're still on the same symbol before capturing prices
    if (currentSymbolRef.current !== selectedSymbol?.scrip) {
      return;
    }

    // Check if we have prices for all required symbols
    const allCodes = new Set();
    strategies.forEach(strategy => {
      if (strategy.rawInstruments) {
        strategy.rawInstruments.forEach(instrument => {
          if (instrument.code) {
            allCodes.add(instrument.code);
          }
        });
      }
    });

    const codesArray = Array.from(allCodes);
    const allPricesAvailable = codesArray.every(code =>
      symbolData[code]?.ltp !== undefined || symbolData[code]?.prev_close !== undefined
    );

    if (allPricesAvailable) {
      // Double-check we're still on the same symbol before setting prices
      if (currentSymbolRef.current !== selectedSymbol?.scrip) {
        return;
      }

      // Capture initial prices
      const prices = {};
      codesArray.forEach(code => {
        prices[code] = symbolData[code]?.ltp || symbolData[code]?.prev_close || 0;
      });

      // Store which symbol these prices belong to
      initialPricesSymbolRef.current = selectedSymbol?.scrip;
      setInitialPrices(prices);
      setHasInitialPrices(true);
    }
  }, [strategies, symbolData, hasInitialPrices, selectedSymbol]);

  // Effect to update strategies when prices become available
  useEffect(() => {
    if (!strategies.length || !symbolData || !selectedSymbol) return;

    const underlyingPrice = symbolData[selectedSymbol.code]?.ltp || symbolData[selectedSymbol.code]?.prev_close;
    if (!underlyingPrice || underlyingPrice <= 0) return;

    // Check if any strategies need to be updated from "Waiting for prices..." to "Calculating..."
    const strategiesToUpdate = strategies.filter(strategy =>
      strategy.capital === "Waiting for prices..." &&
      strategy.rawInstruments &&
      strategy.rawInstruments.length > 0 &&
      areStrategyPricesAvailable(strategy)
    );

    if (strategiesToUpdate.length > 0) {
      setStrategies(prevStrategies =>
        prevStrategies.map(strategy => {
          if (strategy.capital === "Waiting for prices..." && areStrategyPricesAvailable(strategy)) {
            return {
              ...strategy,
              capital: "Calculating...",
              maxProfit: "Calculating...",
              maxLoss: "Calculating...",
              isCalculatingMargin: true,
              isCalculatingMetrics: true,
            };
          }
          return strategy;
        })
      );
    }
  }, [strategies, symbolData, selectedSymbol, areStrategyPricesAvailable]);


  // Simplified calculation effect = margin and max profit/loss
  useEffect(() => {
    if (!strategies.length || !symbolData || !selectedSymbol) return;

    // Prevent multiple simultaneous calculations
    if (isCalculatingRef.current) return;

    // Check if underlying price is available before proceeding
    const underlyingPrice = symbolData[selectedSymbol.code]?.ltp || symbolData[selectedSymbol.code]?.prev_close;
    if (!underlyingPrice || underlyingPrice <= 0) return;

    const needsCalculation = strategies.some(
      (s) => s.isCalculatingMargin || s.isCalculatingMetrics
    );

    if (!needsCalculation) return;

    // Additional check: ensure we have at least some price data for the strategies
    const strategiesWithPrices = strategies.filter(strategy =>
      strategy.rawInstruments &&
      strategy.rawInstruments.length > 0 &&
      areStrategyPricesAvailable(strategy)
    );

    // Only proceed if we have at least one strategy with available prices
    if (strategiesWithPrices.length === 0) return;

    isCalculatingRef.current = true;

    const calculateAllMetrics = async () => {
      // Capture current symbol and category at the start of calculation
      const capturedSymbol = currentSymbolRef.current;
      const capturedCategory = currentCategoryRef.current;

      try {
        // Process strategies one by one to avoid overwhelming the system
        for (let i = 0; i < strategies.length; i++) {
          // Check if symbol/category changed during iteration
          if (capturedSymbol !== currentSymbolRef.current || capturedCategory !== currentCategoryRef.current) {
            // Abort all remaining calculations if symbol/category changed
            break;
          }

          const strategy = strategies[i];

          // Skip if already calculated
          if (calculatedStrategiesRef.current.has(strategy.id)) {
            continue;
          }

          // Skip strategies with no instruments or errors
          if (strategy.capital === "No instruments" || strategy.maxProfit === "No instruments" ||
            strategy.capital === "Error" || strategy.maxProfit === "Error") {
            continue;
          }

          // Check if prices are available for this specific strategy's instruments
          const strategyPricesAvailable = areStrategyPricesAvailable(strategy);

          if (
            strategy.rawInstruments &&
            (strategy.isCalculatingMargin || strategy.isCalculatingMetrics) &&
            strategyPricesAvailable
          ) {
            try {
              const updates = {};

              // Calculating margin if needed
              if (strategy.isCalculatingMargin) {
                const marginResult = await calculateMarginForStrategy(
                  strategy,
                  strategy.rawInstruments,
                  capturedSymbol,
                  capturedCategory
                );

                // Check if calculation was aborted due to symbol/category change
                if (marginResult.aborted) {
                  break;
                }

                updates.capital = marginResult.capital;
                updates.isCalculatingMargin = false;
              }

              // Calculating only max profit and max loss
              if (strategy.isCalculatingMetrics) {
                const metricsResult = await calculateMetricsForStrategy(
                  strategy,
                  strategy.rawInstruments,
                  capturedSymbol,
                  capturedCategory
                );

                // Check if calculation was aborted due to symbol/category change
                if (metricsResult.aborted) {
                  break;
                }

                Object.assign(updates, {
                  maxProfit: metricsResult.maxProfit,
                  maxLoss: metricsResult.maxLoss,
                  isCalculatingMetrics: false,
                });
              }

              // Double-check we're still on the same symbol/category before updating
              if (capturedSymbol !== currentSymbolRef.current || capturedCategory !== currentCategoryRef.current) {
                break;
              }

              // Mark as calculated
              calculatedStrategiesRef.current.add(strategy.id);

              // Update the specific strategy
              setStrategies((prev) =>
                prev.map((s) => (s.id === strategy.id ? { ...s, ...updates } : s))
              );

            } catch (error) {

              // Mark as calculated even if failed to prevent infinite retries
              calculatedStrategiesRef.current.add(strategy.id);

              setStrategies((prev) =>
                prev.map((s) =>
                  s.id === strategy.id
                    ? {
                      ...s,
                      capital: "0",
                      maxProfit: "0",
                      maxLoss: "0",
                      isCalculatingMargin: false,
                      isCalculatingMetrics: false,
                    }
                    : s
                )
              );
            }
          }
        }
      } finally {
        isCalculatingRef.current = false;
      }
    };

    calculateAllMetrics();
  }, [
    strategies,
    symbolData,
    areStrategyPricesAvailable,
    selectedSymbol,
    selectedExpiry,
  ]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Enhanced onTap handler
  const handleStrategyTap = (strategy) => {
    if (strategy.rawInstruments) {
      // Use originalId if available (strategy IDs now include symbol)
      const strategyId = strategy.originalId || strategy.id;
      onStrategySelect(strategyId);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-[14px] font-semibold text-n900">Hedge strategies</h3>
          <button onClick={toggleExpanded} className="p-1">
            <ChevronDown
              size={14}
              className={`transform transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
            />
          </button>
        </div>
        {isExpanded && (
          <div className="mt-1.5 h-44 flex items-center justify-center bg-gray-50 rounded-lg">
            <span className="text-n500">Loading strategies...</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="pb-[10px]" key={`${selectedSymbol?.scrip}-${activeCategory}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-[14px] font-semibold text-p300">Hedge strategies</h3>
        <button onClick={toggleExpanded} className="p-1">
          <ChevronDown
            size={14}
            className={`transform transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
          />
        </button>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="h-44 w-full overflow-hidden">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-n500">Loading strategies...</span>
            </div>
          ) : strategies.length === 0 ? (
            <div className="h-full flex items-center justify-center text-n500">
              Loading hedge strategies...
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="flex gap-3 h-full overflow-x-auto overflow-y-hidden pb-2 w-full"
            >
              {strategies.map((strategy) => (
                <StrategyCard
                  key={strategy.id}
                  strategy={strategy}
                  onTap={handleStrategyTap}
                  symbolData={symbolData}
                  hasInitialPrices={hasInitialPrices}
                  initialPrices={initialPrices}
                  initialPricesSymbol={initialPricesSymbolRef.current}
                  currentSymbol={selectedSymbol?.scrip}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {!isExpanded && (
        <div className="w-[100%] h-auto flex flex-col border-b border-n200">
        </div>
      )}
    </div>
  );
};

export default HedgeStrategiesContainer;
